import { formatDocAmount } from '@/utils/purchase-currency';
import { gstTaxLines, isGstSplit, placeOfSupplyText, type PoSupply } from './gst-split';

/** A PO / bill line's tax: "CGST 2.5% ₹197.50 / SGST 2.5% ₹197.50", "IGST 5% ₹389.00", or "₹x (5%)" without a split. */
export function GstTaxCell({
  supply,
  gstRate,
  lineTax,
  currency,
}: {
  supply: PoSupply | null | undefined;
  gstRate: number;
  lineTax: number;
  currency: string;
}) {
  if (!(lineTax > 0)) return <>—</>;
  if (!isGstSplit(supply)) {
    return (
      <>
        {formatDocAmount(lineTax, currency)} <span className="text-xs text-neutral-500">({gstRate}%)</span>
      </>
    );
  }
  return (
    <>
      {gstTaxLines(supply, [{ gst_rate: gstRate, line_tax: lineTax }]).map((l) => (
        <span key={l.kind} className="block whitespace-nowrap">
          <span className="text-xs text-neutral-500">
            {l.kind} {l.ratePct}%
          </span>{' '}
          {formatDocAmount(l.amount, currency)}
        </span>
      ))}
    </>
  );
}

/** The place-of-supply pill above a PO / bill's lines; nothing on a VAT tenant. */
export function PlaceOfSupply({ supply }: { supply: PoSupply | null | undefined }) {
  if (!isGstSplit(supply)) return null;
  const unknown = supply.source === 'DEFAULT' && supply.supply_type !== 'IMPORT';
  return (
    <p
      className={`mb-3 inline-flex rounded-full px-3 py-1 text-xs font-medium ${
        unknown
          ? 'bg-warning-100 text-warning-800 dark:bg-warning-900/40 dark:text-warning-200'
          : supply.supply_type === 'INTRA'
            ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
            : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200'
      }`}
      data-testid="place-of-supply"
    >
      {placeOfSupplyText(supply)}
    </p>
  );
}
